<template>
  <div class="categories">
    <div class="page__header">
      <div class="page__title">{{ $t(`saleCategory.${"title"}`) }}</div>
      <button v-if="canCreateCategory" class="button button--default"
        @click="store.commit('setShowAddSaleCategoryModal', true)">
        {{ $t(`saleCategory.${"addBtn"}`) }}
      </button>
    </div>

    <div class="categories__container">
      <div class="sales box box--default">
        <div class="label label--gradient">{{ $t(`saleCategory.${"choseSaleCategory"}`) }}</div>

        <Loading v-if="salesLoading" />
        <NoData v-else-if="collectionError?.status === 403" type="no-permission" />
        <div v-else class="sales__body">
          <div class="sales__teams">
            <VSelect class="select select--default" :options="categoryTeams" :reduce="(team) => team.id"
              @update:modelValue="store.commit('setSaleCategoryTeamId', $event)" :model-value="saleCategoryTeamId"
              label="name" :placeholder="$t('saleCategory.selectTeam')" />
          </div> 
          <div @click="selectSale(sale.id)" class="sales__sale"
            :class="{ 'sales__sale--selected': sale.id === selectedSale?.id }" v-for="sale in saleCategories"
            :key="sale.id">
            {{ sale.name }}
          </div>
        </div>
      </div>
      <div class="prices box box--default">
        <div class="label label--gradient">{{ selectedSale?.name }}</div>

        <div class="prices__body">
          <div class="page__header">
            <div class="page__title">{{ selectedSale?.name }}</div>
            <div v-if="canDeleteCategory" class="icon icon__delete" @click="deleteCategoryModal()"></div>
          </div>

          <div class="prices__header">
            <div class="column column--branch">
              {{ $t(`saleCategory.${"branch"}`) }}
            </div>
            <div class="column column--agent">
              {{ $t(`saleCategory.${"agent"}`) }}
            </div>
            <div class="column column--company">
              {{ $t(`saleCategory.${"company"}`) }}
            </div>
          </div>

          <div class="prices__table">
            <PriceRow v-for="price in selectedSale?.sales_category_prices" :key="price.id" :price="price"
              :selectSale="selectSale" :teamId="saleCategoryTeamId" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import Loading from "@/partials/Loading";
import PriceRow from "@/components/PriceCategories/PriceRow";
import NoData from "@/partials/NoData";

const store = useStore();
const route = useRoute();
const router = useRouter();

// Reactive state
const salesLoading = ref(false);
const selectedSale = ref({});

// Computed properties
const saleCategories = computed(() => store.getters.saleCategories.data);
const saleCategoryTeamId = computed(() => store.getters.saleCategoryTeamId);
const collectionError = computed(() => store.getters.saleCategoryCollectionError);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);

const categoryTeams = computed(() => {
  if (isSuperAdmin.value) {
    return (store.getters.teams || []).filter((team) => team && team.id != null);
  }

  const authorizedIds = store.getters.authorizedTeamIds("sales_category_access");
  const memberships = Array.isArray(store.getters.authorizationMemberships)
    ? store.getters.authorizationMemberships
    : [];

  if (authorizedIds === null) {
    return (store.getters.teams || []).filter((team) => team && team.id != null);
  }

  const allowedIds = new Set(Array.isArray(authorizedIds) ? authorizedIds : []);

  return memberships
    .filter((membership) => membership?.team?.id != null && allowedIds.has(membership.team.id))
    .map((membership) => membership.team);
});
const canCreateCategory = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("sales_category_create", saleCategoryTeamId.value);
});
const canDeleteCategory = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("sales_category_delete", saleCategoryTeamId.value);
});

// Watchers
watch(
  () => saleCategoryTeamId.value,
  async (teamId) => {
    salesLoading.value = true;
    if (teamId) {
      await router.replace({ query: { ...route.query, team_id: teamId } }).catch(() => { });
      await store.dispatch("getSaleCategoriesByTeam", teamId);
      await store.dispatch("getSaleCategoriesFormData", teamId);
    } else {
      store.commit("setSaleCategories", []);
      store.commit("setSaleCategoriesData", {});
    }
    setFirstSaleCategory();
    salesLoading.value = false;
  }
);

// Methods
const setFirstSaleCategory = () => {
  if (saleCategories.value.length) {
    const [firstSale] = saleCategories.value;
    selectedSale.value = firstSale;
  } else {
    selectedSale.value = {};
  }
};

const selectSale = (saleId) => {
  selectedSale.value = saleCategories.value.find(
    (sale) => sale.id === saleId
  );
};

const deleteCategory = async (categoryId) => {
  await store.dispatch("deleteSaleCategory", { id: categoryId, teamId: saleCategoryTeamId.value });

  setTimeout(async () => {
    salesLoading.value = true;
    await store.dispatch("getSaleCategoriesByTeam", saleCategoryTeamId.value);
    setFirstSaleCategory();
    salesLoading.value = false;
  }, 300);
};

const deleteCategoryModal = () => {
  store.commit("setDeleteModal", {
    display: true,
    message: 'Sale category with name ' + `'${selectedSale.value.name}'`,
    action: deleteCategory,
    id: selectedSale.value.id,
  });
};

// Lifecycle hooks
onMounted(async () => {
  salesLoading.value = true;

  if ((store.getters.isSuperAdmin) && !store.getters.teams.length) {
    await store.dispatch("getTeams");
  }

  const routeTeamId = Number(route.query.team_id);
  const initialTeam = categoryTeams.value.find((team) => team.id === routeTeamId)
    || categoryTeams.value.find((team) => team.id === store.getters.currentTeamId)
    || categoryTeams.value[0];
  if (initialTeam) {
    if (saleCategoryTeamId.value === initialTeam.id) {
      await store.dispatch("getSaleCategoriesByTeam", initialTeam.id);
      await store.dispatch("getSaleCategoriesFormData", initialTeam.id);
      setFirstSaleCategory();
    } else {
      store.commit("setSaleCategoryTeamId", initialTeam.id);
    }
  }

  salesLoading.value = false;
});
</script>

<style scoped lang="scss">
.categories {
  height: 100%;

  .page__header {
    .button {
      width: 180px;
    }

    .icon__delete {
      margin-right: 30px;
    }
  }

  .title__delete {
    padding-right: 20px;
  }

  &__container {
    height: calc(100% - 48px - 20px);
    display: flex;
  }

  .sales,
  .prices {
    height: 100%;
    background: var(--bg-1);
    position: relative;
  }

  .sales__sale,
  .prices__price {
    color: var(--text-9);
    font-size: var(--medium);
    padding: 10px 20px;
    cursor: pointer;

    &:hover,
    &--selected {
      background: var(--bg-17);
    }
  }

  .sales,
  .prices {
    &__body {
      margin-top: 65px;
      padding-top: 20px;
      overflow: auto;
    }
  }

  .sales {
    width: 400px;
    margin-right: 20px;

    &__teams {
      padding: 0 20px;
      margin-bottom: 22px;
    }

    &__body {
      height: calc(100% - 65px);
    }
  }

  .prices {
    flex: 1;

    :deep(.column--branch) {
      flex: 1;
      border-right: 1px solid var(--border-6);
    }

    :deep(.column--agent) {
      flex: 4;
      text-align: right;
      padding-right: 90px;
      border-right: 1px solid var(--border-6);
    }

    :deep(.column--company) {
      flex: 2;
      padding-left: 100px;
    }

    &__body {
      padding-top: 0;
      height: calc(100% - 65px);
      overflow: initial;

      &--stretch {
        height: calc(100% - 65px);
      }
    }

    &__header {
      box-shadow: 0px 2px 4px var(--shadow-1);
      border-radius: var(--radius-1);
      display: flex;
      padding: 10px 20px;
      color: var(--text-2);
    }

    .page__header {
      padding-left: 20px;
    }

    &__table {
      height: calc(100% - 24px - 20px - 36px);
      overflow: auto;

      :deep(.column) {
        border-right: none;
        position: relative;
      }

      :deep(.column--branch) span {
        position: absolute;
        top: -8px;
        white-space: nowrap;
      }

      :deep(.column--agent) span,
      :deep(.column--company) span {
        position: relative;
      }

      :deep(.column--agent) span {
        left: -2px;
        text-decoration: underline;
        color: var(--text-10);
        cursor: pointer;
      }

      :deep(.column--company) span {
        left: 20px;
      }
    }
  }
}
</style>
